@if (inputData$ | async; as inputData) {
  <!-- Show generating view when generating -->
  @if (isGenerating$ | async) {
    <div
      class="max-w-xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1"
    >
      <!-- Main Content Card -->
      <div class="text-center py-8">
        <!-- Spinner -->
        <div class="relative w-16 h-16 mx-auto mb-6">
          <div
            class="absolute inset-0 rounded-full border-4 border-gray-100"
          ></div>
          <div
            class="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin"
            [style.borderColor]="themeColor"
            [style.borderTopColor]="'transparent'"
          ></div>
        </div>

        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          Generating your assignment
        </h2>
        <p class="text-gray-500">
          Our AI is crafting a high-quality assignment based on your inputs and
          state standards.
        </p>
      </div>

      <!-- Progress Card -->
      <div
        class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100"
      >
        <!-- Progress Bar -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-semibold text-gray-700">Progress</span>
            <span class="text-sm font-bold" [style.color]="themeColor">
              {{ progressPercentage$ | async }}%
            </span>
          </div>
          <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all duration-500 ease-out"
              [style.backgroundColor]="themeColor"
              [style.width]="(progressPercentage$ | async) + '%'"
            ></div>
          </div>
        </div>

        <!-- Progress Steps -->
        <div class="space-y-4">
          @for (step of displaySteps$ | async; track step.key) {
            <div class="flex items-center gap-3">
              <!-- Status Icon -->
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300"
                [class.bg-green-100]="step.status === 'completed'"
                [class.bg-amber-100]="step.status === 'in_progress'"
                [class.bg-gray-100]="step.status === 'pending'"
              >
                @if (step.status === 'completed') {
                  <lucide-icon
                    [img]="icons.Check"
                    class="w-4 h-4 text-green-600"
                  ></lucide-icon>
                } @else if (step.status === 'in_progress') {
                  <div
                    class="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"
                  ></div>
                } @else {
                  <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                }
              </div>

              <!-- Step Label -->
              <span
                class="text-sm transition-colors duration-300"
                [class.text-gray-800]="step.status === 'completed'"
                [class.font-semibold]="step.status === 'in_progress'"
                [class.text-gray-800]="step.status === 'in_progress'"
                [class.text-gray-400]="step.status === 'pending'"
              >
                {{ step.label }}
              </span>
            </div>
          }
        </div>
      </div>

      <!-- Helper Text -->
      <p class="text-center text-sm text-gray-400">
        We're analyzing your objective to generate relevant questions for
        assignment
      </p>

      <!-- Error Message -->
      @if (error$ | async; as error) {
        <div
          class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
        >
          {{ error }}
        </div>
      }
    </div>
  } @else {
    <!-- Normal objective input view -->
    <div
      class="max-w-xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1"
    >
      <!-- Header -->
      <div class="text-center mb-8">
        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          Define Your Objective
        </h2>
        <p class="text-gray-500">
          Describe what you want to assess and how many questions to generate.
        </p>
      </div>

      <div
        class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 space-y-6"
      >
        <!-- Objective Input -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="text-sm font-bold text-gray-700">Objective</label>
            @if (!(exampleUsed$ | async)) {
              <button
                type="button"
                (click)="onUseExample()"
                [disabled]="(isLoadingExample$ | async) ?? false"
                class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg transition-all cursor-pointer disabled:cursor-not-allowed"
                [class.text-amber-700]="!(isLoadingExample$ | async)"
                [class.bg-amber-50]="!(isLoadingExample$ | async)"
                [class.hover:bg-amber-100]="!(isLoadingExample$ | async)"
                [class.text-amber-400]="(isLoadingExample$ | async) ?? false"
                [class.bg-amber-50]="(isLoadingExample$ | async) ?? false"
              >
                @if (isLoadingExample$ | async) {
                  <div
                    class="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"
                  ></div>
                  <span>Generating...</span>
                } @else {
                  <lucide-icon
                    [img]="icons.Lightbulb"
                    class="w-3.5 h-3.5"
                  ></lucide-icon>
                  <span>Use example</span>
                }
              </button>
            }
          </div>
          <p class="text-xs text-gray-500 mb-2">
            Describe the goal of this assignment.
          </p>
          <div class="relative">
            <textarea
              #objectiveTextarea
              [value]="inputData.objective"
              (input)="updateObjective($event)"
              placeholder='e.g. "Students will analyze the causes and effects of the American Revolution, focusing on key events and figures..."'
              rows="3"
              class="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition-all resize-none overflow-hidden text-gray-800 placeholder-gray-400"
              [style.--tw-ring-color]="themeColor + '33'"
              [style.min-height.px]="96"
              [class.focus:border-green-500]="true"
            ></textarea>
            <!-- Attach files button (hidden for now) -->
            <button
              type="button"
              class="hidden absolute bottom-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              title="Attach files"
              (click)="onAttachFiles()"
            >
              <lucide-icon
                [img]="icons.Paperclip"
                class="w-4 h-4"
              ></lucide-icon>
            </button>
          </div>
        </div>

        <!-- Question Count -->
        <div>
          <label class="block text-sm font-bold text-gray-700 mb-1"
            >Number of questions</label
          >
          <p class="text-xs text-gray-500 mb-2">
            How many questions should the AI generate?
          </p>
          <div class="flex flex-wrap gap-2">
            @for (
              option of visibleQuestionCounts$ | async;
              track option
            ) {
              <button
                type="button"
                (click)="selectQuestionCount(option)"
                class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
                [class.text-white]="inputData.questionCount === option"
                [class.shadow-md]="inputData.questionCount === option"
                [class.bg-white]="inputData.questionCount !== option"
                [class.text-gray-600]="inputData.questionCount !== option"
                [class.border-gray-200]="inputData.questionCount !== option"
                [class.hover:border-gray-300]="
                  inputData.questionCount !== option
                "
                [class.hover:bg-gray-50]="
                  inputData.questionCount !== option
                "
                [style.backgroundColor]="
                  inputData.questionCount === option ? themeColor : ''
                "
                [style.borderColor]="
                  inputData.questionCount === option ? themeColor : ''
                "
              >
                {{ option }}
              </button>
            }
            @if (hasHiddenQuestionCounts) {
              <button
                type="button"
                (click)="toggleShowMoreQuestionCounts()"
                class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
              >
                @if (showMoreQuestionCounts$ | async) {
                  <span>Show less</span>
                  <lucide-icon
                    [img]="icons.ChevronUp"
                    class="w-4 h-4"
                  ></lucide-icon>
                } @else {
                  <span>Show more</span>
                  <lucide-icon
                    [img]="icons.ChevronDown"
                    class="w-4 h-4"
                  ></lucide-icon>
                }
              </button>
            }
          </div>
        </div>

        <!-- Error Message -->
        @if (error$ | async; as error) {
          <div
            class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
          >
            {{ error }}
          </div>
        }
      </div>

      <!-- Spacer for fixed footer -->
      <div class="h-24"></div>
    </div>

    <!-- Fixed Footer -->
    <div class="step-footer">
      <div class="max-w-xl mx-auto flex gap-3 px-1">
        <button
          (click)="onBack()"
          class="flex items-center justify-center gap-2 px-6 py-3 text-gray-600 font-medium bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.ArrowLeft" class="w-4 h-4"></lucide-icon>
          Back
        </button>
        <button
          (click)="onGenerateAndReview()"
          [disabled]="!(canProceed$ | async)"
          class="flex-1 flex items-center justify-center gap-2 py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          [style.backgroundColor]="themeColor"
        >
          Generate and review
          <lucide-icon [img]="icons.Sparkles" class="w-5 h-5"></lucide-icon>
        </button>
      </div>
    </div>
  }
}
